import { FC, useCallback, useRef, useState } from 'react';
import { InlineNotification } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import { useAtom } from 'jotai';
import {
  EntityForm,
  EntityFormHandle,
  EntitySeedValues,
  useEntityFormServerErrors,
} from 'components/entity-form';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import { DocumentResponse } from 'types/api-responses';
import {
  CLIENT_SESSION_ID,
  canonizePartyAtom,
  partyUrl,
} from './atoms/structured-parties';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import { SharedAnchorNotice } from './shared-anchor-notice';
import {
  anchorCard,
  anchorName as anchorNameFor,
  anchorSourceLabel,
} from './utils';
import 'components/inline-edit';
import type { StructuredPartyDetail } from './types';
import style from './canonize-panel.module.scss';

interface Props {
  open: boolean;
  document: DocumentResponse;
  party: StructuredPartyDetail;
  // The Match Review working session, so the Add as New joins its undo stack.
  sessionId: string;
  onClose: () => void;
  onCanonized: () => void;
}

const CANONIZE_DESCRIPTION =
  'Add as New creates a real canonical entity — an Individual or Organization — ' +
  'from this Imported entity. The new entity will appear as a match option in ' +
  'front of the suggestions, will be auto-selected, and will be automatically ' +
  'marked as the correct match for this party.';

/**
 * The Add as New form's seed: the party's current anchor, carrying each
 * record's id so edits route to the real (cited) records rather than creating
 * duplicate copies.
 *
 * @param {StructuredPartyDetail} party - The party being added as new.
 * @return {EntitySeedValues} The form's starting values.
 */
const canonizeSeed = (party: StructuredPartyDetail): EntitySeedValues => {
  const anchor = anchorCard(party);
  const anchorName = anchorNameFor(party);
  return {
    name: anchorName || null,
    notes: party.name ? `Original name before parsing: ${party.name}` : null,
    addresses: (anchor?.addresses ?? []).map((a) => ({
      id: a.id,
      streetAddress: a.streetAddress,
      city: a.city,
      state: a.state,
      zipCode: a.zipCode,
      primary: a.primary,
    })),
    phoneNumbers: (anchor?.phoneNumbers ?? []).map((p) => ({
      id: p.id,
      number: p.number,
      primary: p.primary,
    })),
    emailAddresses: (anchor?.emailAddresses ?? []).map((e) => ({
      id: e.id,
      email: e.email,
      primary: e.primary,
    })),
  };
};

export const CanonizePanel: FC<Props> = ({
  open,
  document,
  party,
  sessionId,
  onClose,
  onCanonized,
}) => {
  const formRef = useRef<EntityFormHandle>(null);
  const [formValid, setFormValid] = useState(false);
  const {
    error,
    setError,
    fieldErrors,
    clearFieldErrors,
    clearErrors,
    showServerError,
  } = useEntityFormServerErrors();
  const [canonizeState, triggerCanonize] = useAtom(canonizePartyAtom);
  const pending = canonizeState.loading;
  const pageContentSelector = useExclusiveDrawer('canonize', open, onClose);

  // The current anchor: the staged entity, or the Deduplicated Result an In
  // Progress party was matched into.
  const anchor = anchorCard(party);
  const anchorLabel = anchorSourceLabel(party.currentAnchor?.source);
  const anchorName = anchorNameFor(party);

  // Seeded once per opening (and per party/anchor) so a refetch while the
  // panel is open doesn't reset what the examiner typed. A reseed also drops
  // the last attempt's errors.
  const seedKey = open ? `${party.kind}-${party.id}-${anchor?.id}` : null;
  const [seed, setSeed] = useState(() => ({
    key: seedKey,
    values: canonizeSeed(party),
  }));
  if (seed.key !== seedKey) {
    setSeed({ key: seedKey, values: canonizeSeed(party) });
    clearErrors();
  }

  const handleSubmit = useCallback(
    (payload: Record<string, unknown>) => {
      clearErrors();
      triggerCanonize({
        url: partyUrl(document.id, party.id, party.kind, '/canonization'),
        type: 'POST',
        data: {
          entity: payload,
          session_id: sessionId,
          client_session_id: CLIENT_SESSION_ID,
        },
        onSuccess: () => onCanonized(),
        onError: (err) => showServerError(err, 'Could not add entity'),
      });
    },
    [
      document.id,
      party.id,
      party.kind,
      sessionId,
      triggerCanonize,
      onCanonized,
      clearErrors,
      showServerError,
    ]
  );

  const actions = [
    {
      kind: 'secondary' as const,
      label: 'Cancel',
      onClick: onClose,
      disabled: pending,
    },
    {
      kind: 'primary' as const,
      label: 'Add as New',
      onClick: () => formRef.current?.submit(),
      disabled: pending || !formValid,
    },
  ];

  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector=".canonize-sidepanel"
        pageContentSelector={pageContentSelector}
        defaultWidthPx={640}
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Add as New"
        subtitle={
          anchorName ? (
            <>
              {anchorLabel}
              <br />
              {anchorName}
            </>
          ) : (
            anchorLabel
          )
        }
        className="canonize-sidepanel"
        slideIn
        selectorPageContent={pageContentSelector}
        placement="right"
        size="lg"
        actions={actions}
      >
        <div className={style.body}>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title=""
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
            />
          )}
          <SharedAnchorNotice sharedWith={party.sharedWith} />
          <p className={style.description}>{CANONIZE_DESCRIPTION}</p>
          {/* Mount only while open so the form re-seeds (and resets to the
              default Organization type) each time the panel is opened. */}
          {open && (
            <div className={style.createWrap}>
              <EntityForm
                ref={formRef}
                entity={undefined}
                getEntity={undefined}
                loading={false}
                entityTypeValue="OrganizationEntity"
                seedValues={seed.values}
                hideSubmitButton
                showCancel={false}
                stacked
                onValidityChange={setFormValid}
                onSubmitPayload={handleSubmit}
                fieldErrors={fieldErrors}
                onFieldErrorsStale={clearFieldErrors}
              />
            </div>
          )}
        </div>
      </SidePanel>
    </>
  );
};
